// Routetabel van de blog-wizard. Puur (geen React), zodat het testbaar is.
//
// Uitgangspunten:
// - De sessie in de database is de waarheid. Elk wizardscherm krijgt het
//   sessie-id via de URL (?session=<id>); location.state is alleen fallback.
// - Terug is één stap terug volgens een vaste tabel, nooit browser-history.
// - Generatorschermen hebben geen terug (alleen Sluiten). image-generate is de
//   uitzondering: daar staat eerst een keuzescherm (prompt, upload).

export const WIZARD_EXIT_ROUTE = '/my-blogs';

export const WIZARD_LEAVE_HINT =
  'Je kunt dit scherm gerust verlaten. Je vindt deze blog terug onder Concepten in Mijn Blogs.';

export const WIZARD_PATHS = {
  ideaSelect: '/idea-select',
  direction: '/article-direction',
  articleGenerate: '/article-generate',
  articleEdit: '/article-edit',
  titleGenerate: '/title-generate',
  selectTitle: '/select-title',
  introGenerate: '/intro-generate',
  selectIntro: '/select-intro',
  imageGenerate: '/image-generate',
  selectImage: '/select-image',
  preview: '/blog-preview',
  success: '/success',
} as const;

export type WizardPath = (typeof WIZARD_PATHS)[keyof typeof WIZARD_PATHS];

const WIZARD_PATH_SET = new Set<string>(Object.values(WIZARD_PATHS));

export function isWizardPath(path: string): path is WizardPath {
  return WIZARD_PATH_SET.has(path);
}

// Eén stap terug per scherm. null = geen terugpijl (alleen Sluiten).
const BACK_ROUTES: Record<WizardPath, string | null> = {
  [WIZARD_PATHS.ideaSelect]: '/dashboard/new-blog',
  // Standaard. ArticleDirection kiest zelf '/ideeen' als de sessie uit één bewaard idee komt.
  [WIZARD_PATHS.direction]: WIZARD_PATHS.ideaSelect,
  [WIZARD_PATHS.articleGenerate]: null,
  [WIZARD_PATHS.articleEdit]: WIZARD_PATHS.direction,
  [WIZARD_PATHS.titleGenerate]: null,
  [WIZARD_PATHS.selectTitle]: WIZARD_PATHS.articleEdit,
  [WIZARD_PATHS.introGenerate]: null,
  [WIZARD_PATHS.selectIntro]: WIZARD_PATHS.selectTitle,
  [WIZARD_PATHS.imageGenerate]: WIZARD_PATHS.selectIntro,
  [WIZARD_PATHS.selectImage]: WIZARD_PATHS.selectIntro,
  [WIZARD_PATHS.preview]: WIZARD_PATHS.selectImage,
  [WIZARD_PATHS.success]: WIZARD_PATHS.preview,
};

export function backRouteFor(pathname: string): string | null {
  return isWizardPath(pathname) ? BACK_ROUTES[pathname] : null;
}

// Wizard-URL met sessie-id. Niet-wizardpaden (dashboard, ideeën) krijgen geen id.
export function wizardUrl(path: string, sessionId: string | null | undefined): string {
  // Verwacht een kaal pad (geen query, geen slash aan het eind).
  if (!sessionId || !isWizardPath(path)) return path;
  return `${path}?session=${encodeURIComponent(sessionId)}`;
}

// Terug-URL voor het huidige scherm, of null als het scherm geen terugpijl heeft.
export function backUrlFor(pathname: string, sessionId: string | null | undefined): string | null {
  const target = backRouteFor(pathname);
  return target ? wizardUrl(target, sessionId) : null;
}

// Sessie-id uit de URL (?session=), anders uit location.state.sessionId, anders null.
export function sessionIdFromLocation(location: { search: string; state?: unknown }): string | null {
  const fromQuery = new URLSearchParams(location.search).get('session')?.trim();
  if (fromQuery) return fromQuery;
  const state = location.state as { sessionId?: unknown } | null | undefined;
  const fromState = state?.sessionId;
  return typeof fromState === 'string' && fromState.length > 0 ? fromState : null;
}
